import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { PageDrawerFooter } from 'common/components/PageDrawer/PageDrawerFooter';
import {
    IUserListItemFragment,
    IUserSelectorInput,
    IUserSelectorQueryVariables,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { PageDrawer } from 'common/components/PageDrawer';
import { UserTable } from 'user/components/UserTable';
import { useUserTableData } from 'user/hooks/useUserTableData';
import { useUserList } from 'user/hooks/useUserList';
import { SelectedUsersTable } from 'user/components/UserTable/SelectedUsersTable';

interface IProps extends IPageDrawerProps {
    addButtonTranslationKeys?: {
        noSelection?: string;
        selection?: string;
    };
    selectorQueryVariables?: IUserSelectorInput;
    additionalDisabled?: (totalSelectedUsers: number) => boolean;
    infoMessage?: React.ReactNode;
    footerAppend?:
        | React.ReactNode
        | ((totalSelectedUsers: number) => React.ReactNode);
    queryVariables?: Partial<IUserSelectorQueryVariables>;
    loading?: boolean;
    noResultsLabel?: string;
    paginationTranslationKey?: string;
    usersCount?: number;
    renderFooterButton?: (selectedUserIds: string[]) => React.ReactNode;
    resetRef?: RefObject<(() => void) | null>;
    onAddUsers(
        selectedUsers: string[],
        selectableUsers?: IUserListItemFragment[]
    ): void;
    onDrawerOpen(open: boolean): void;
}

export const UserSelectDrawer = ({
    addButtonTranslationKeys,
    additionalDisabled,
    selectorQueryVariables,
    infoMessage,
    footerAppend,
    queryVariables,
    loading,
    noResultsLabel,
    renderFooterButton,
    resetRef,
    paginationTranslationKey,
    open,
    title,
    onAddUsers,
    onDrawerOpen,
}: IProps) => {
    const [translate] = useTranslation();

    const {
        loading: loadingUserData,
        users,
        alreadyAddedUsers,
        tableDataProps,
        selectedUsersProps,
    } = useUserTableData(
        useUserList,
        !open,
        queryVariables,
        selectorQueryVariables,
        true // withSelected
    );

    const observerRef = useRef<ResizeObserver | null>(null);
    const [footerHeight, setFooterHeight] = useState(0);
    const theme = useTheme();

    // Callback ref that measures the fixed footer's height via ResizeObserver
    // so the list content gets enough bottom padding to not be hidden behind it.
    const footerRef = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();

        if (!node) return;

        observerRef.current = new ResizeObserver(() => {
            setFooterHeight(node.getBoundingClientRect().height);
        });

        observerRef.current.observe(node);
        setFooterHeight(node.getBoundingClientRect().height);
    }, []);

    const listPaddingBottom = footerHeight
        ? `calc(${theme.spacing(2)} + ${footerHeight}px)`
        : theme.spacing(13);

    const { searchProps, paginationProps, selectionProps } = tableDataProps;
    const { handleSearch, handleFilterSelect } = searchProps || {};
    const { setPage } = paginationProps || {};
    const { selected, onSelectAll } = selectionProps || {};

    const {
        persistedSelectedUsers,
        onAddSelectedUsers,
        onDeselectUser,
        clearSelectedUsers,
    } = selectedUsersProps || {};

    const resetSettings = useCallback(() => {
        handleSearch('');
        handleFilterSelect([]);
        setPage?.(1);

        clearSelectedUsers?.();

        // Deselect all when there are selected items
        if (selected?.length) onSelectAll();
    }, [
        handleSearch,
        handleFilterSelect,
        setPage,
        clearSelectedUsers,
        selected,
        onSelectAll,
    ]);

    useEffect(() => {
        if (resetRef) {
            resetRef.current = resetSettings;
        }
    }, [resetRef, resetSettings]);

    const selectedUserTable = useMemo(() => {
        if (!persistedSelectedUsers?.length) return null;

        return (
            <Box sx={{ mb: 4, mt: 4 }}>
                <SelectedUsersTable
                    selectedUsers={persistedSelectedUsers}
                    onDeSelectUser={onDeselectUser}
                />
            </Box>
        );
    }, [persistedSelectedUsers]);

    // The users in the selected table and the already added users
    const disabledUserIds = useMemo(
        () => [
            ...alreadyAddedUsers,
            ...(persistedSelectedUsers?.map(
                (persistedSelectedUser) => persistedSelectedUser.id
            ) || []),
        ],
        [alreadyAddedUsers, persistedSelectedUsers]
    );

    // These are the users that are in the persisted table as well
    // as users that are currently selected in the regular table
    const totalSelectedUsers = useMemo(
        () => selected.length + (persistedSelectedUsers?.length || 0),
        [selected, persistedSelectedUsers]
    );

    const allSelectedUserIds = useMemo(
        () => [
            ...new Set([
                ...(persistedSelectedUsers?.map(
                    (persistedSelectedUser) => persistedSelectedUser.id
                ) || []),
                ...selected,
            ]),
        ],
        [persistedSelectedUsers, selected]
    );

    return (
        <PageDrawer
            open={open}
            title={title || translate('addUsersTitle')}
            onClose={() => {
                onDrawerOpen(false);

                resetSettings();
            }}
        >
            <Box
                sx={{
                    pb: listPaddingBottom,
                    pt: { xs: 2, sm: 4 },
                    px: { xs: 2, sm: 4 },
                }}
            >
                <UserTable
                    inDrawer
                    disabled={loading || loadingUserData}
                    disabledUserIds={disabledUserIds}
                    infoMessage={infoMessage}
                    loading={loadingUserData}
                    noResultsLabel={noResultsLabel}
                    paginationProps={paginationProps}
                    paginationTranslationKey={paginationTranslationKey}
                    searchProps={searchProps}
                    selectedUserTable={selectedUserTable}
                    selectionProps={selectionProps}
                    users={users}
                    onFilterChange={() => {
                        onAddSelectedUsers?.(selected);
                    }}
                    onSearchClear={() => {
                        onAddSelectedUsers?.(selected);
                    }}
                    onSelectAll={onSelectAll}
                />
            </Box>
            <PageDrawerFooter ref={footerRef} sx={{ zIndex: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {!!footerAppend && (
                        <Box>
                            {typeof footerAppend === 'function'
                                ? footerAppend(totalSelectedUsers)
                                : footerAppend}
                        </Box>
                    )}

                    {renderFooterButton ? (
                        renderFooterButton(allSelectedUserIds)
                    ) : (
                        <Button
                            color="primary"
                            disabled={
                                loading ||
                                (!persistedSelectedUsers?.length &&
                                    !selected.length) ||
                                additionalDisabled?.(totalSelectedUsers)
                            }
                            loading={loading}
                            variant="contained"
                            onClick={async () => {
                                await onAddUsers(allSelectedUserIds, users);

                                resetSettings();
                            }}
                        >
                            {!totalSelectedUsers
                                ? translate(
                                      addButtonTranslationKeys?.noSelection ||
                                          'addUsersTitle'
                                  )
                                : translate(
                                      addButtonTranslationKeys?.selection ||
                                          'addUsers',
                                      {
                                          count: totalSelectedUsers,
                                      }
                                  )}
                        </Button>
                    )}
                </Box>
            </PageDrawerFooter>
        </PageDrawer>
    );
};
